import { UserUi } from 'src/app/shared/models/user-ui';
import { apis, controllerTypes, HttpRequestSettings } from '../models/request-settings';
import { HttpWebApiService } from '@sb-shared/services/http-web-api.service';
import { inject, Injectable } from '@angular/core';
import { MemberTypeId } from '@sb-shared/enums/member.type.enum';
import { CookieConsentService } from '@sb-shared/services/cookie-consent.service';
import { ScriptLoaderService } from '@sb-shared/services/script-loader.service';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const PENDO_API_KEY = '6be3cafd-97d5-44ea-5425-37835fe88ef8';
const PENDO_AGENT_URL = `https://cdn.pendo.io/agent/static/${PENDO_API_KEY}/pendo.js`;

/** Pendo rejects ids longer than this many UTF-8 bytes (Faria Pendo standard, rules 4 and 8). */
const maxPendoIdBytes = 128;

/** Faria Pendo standard rules 4 and 8: at most 128 UTF-8 bytes, no backslash, no double quote. */
const isValidPendoId = (id: string): boolean =>
  new TextEncoder().encode(id).length <= maxPendoIdBytes && !id.includes('\\') && !id.includes('"');

/** Faria Pendo standard rule 1: the visitor id is the email, trimmed and lowercased. Blank -> null. */
const toVisitorId = (email: unknown): string | null => {
  if (typeof email !== 'string') {
    return null;
  }

  const id = email.trim().toLowerCase();
  return id ? id : null;
};

/** Faria Pendo standard rules 7 and 8: the KBL id, else sb-<organisation id>. Neither usable -> null. */
const toAccountId = (kblId: unknown, sbOrganisationId: unknown): string | null => {
  if (kblId) {
    return kblId.toString();
  }

  return Number.isInteger(sbOrganisationId) && (sbOrganisationId as number) > 0 ? `sb-${sbOrganisationId}` : null;
};

@Injectable({
  providedIn: 'root'
})
export class PendoService {
  config: HttpRequestSettings = {
    api: apis.Core,
    controllerType: controllerTypes.User
  };

  private readonly http = inject(HttpWebApiService);
  private readonly cookieConsent = inject(CookieConsentService);
  private readonly scriptLoader = inject(ScriptLoaderService);

  private pendingUser: UserUi;
  private consentSubscription: Subscription;
  private agent$: ReplaySubject<boolean>;

  /**
   * Identifies the user to Pendo. Nothing is loaded or sent while Analytics consent is not given (SB-17045),
   * including after it is withdrawn; a setup made meanwhile runs once consent arrives, for the latest user.
   */
  setup(user: UserUi) {
    if (!this.isEnabled()) {
      console.log('Pendo disabled');
      return;
    }

    // Read the current choice on every call: Analytics can be switched off again later in the session.
    if (this.cookieConsent.analyticsConsent$.value) {
      this.identify(user);
      return;
    }

    this.pendingUser = user;

    if (!this.consentSubscription || this.consentSubscription.closed) {
      this.consentSubscription = this.cookieConsent.analyticsConsent$
        .pipe(filter(Boolean), take(1))
        .subscribe(() => this.identify(this.pendingUser));
    }
  }

  /** The current hostname, behind a method so specs can change it. */
  protected currentHostname(): string {
    return window.location.hostname;
  }

  private isEnabled(): boolean {
    if (!window.EveryBuddy.EnablePendo) {
      return false;
    }

    const exclusionSubdomains = environment.pendoExclusionSubdomains;

    if (exclusionSubdomains) {
      const exclusions = exclusionSubdomains.split(',').map(s => s.trim().toLowerCase());
      const currentHost = this.currentHostname().toLowerCase();

      if (exclusions.some(exclusion => currentHost.startsWith(exclusion))) {
        return false;
      }
    }

    return true;
  }

  private loadAgent(): Observable<boolean> {
    if (!this.agent$) {
      this.agent$ = new ReplaySubject<boolean>(1);

      const script = this.scriptLoader.loadJsScript(PENDO_AGENT_URL);
      script.onload = () => this.agent$.next(!!window.pendo);
      script.onerror = error => {
        console.error('Pendo agent failed to load', error);
        this.agent$.next(false);
      };
    }

    return this.agent$;
  }

  private identify(user: UserUi) {
    this.loadAgent()
      .pipe(take(1), filter(Boolean))
      .subscribe(() => {
        console.log('Pendo enabled');

        this.http.get('Info/Tracking', this.config).subscribe(res => {
          let userAccountRole = res.userAccountRole;

          // Override role with current member type - in case of staff/parent viewing as parent.
          switch (user.currentMemberTypeId) {
            case MemberTypeId.Staff:
              userAccountRole = 'Staff';
              break;
            case MemberTypeId.Parent:
              userAccountRole = 'Parent';
              break;
            case MemberTypeId.Student:
              userAccountRole = 'Student';
          }

          const visitorId = toVisitorId(res.email);
          const accountId = toAccountId(res.kblId, res.sbOrganisationId);
          if (!visitorId || !accountId || !isValidPendoId(visitorId) || !isValidPendoId(accountId)) {
            console.log('Pendo not initialised: no valid visitor or account id');
            return;
          }

          window.pendo.initialize({
            visitor: {
              id: visitorId,
              app: 'SB',
              sb_user_id: res.id,
              sb_user_first_name: res.firstName,
              sb_user_last_name: res.lastName,
              sb_user_created_at: res.createdAtUtc,
              sb_user_last_login_at: res.lastLoggedInDateTimeUtc,
              sb_user_account_role: userAccountRole,
              sb_user_lang: res.userLang,
              sb_user_roles: res.userRoles,
              sb_user_year_group: res.userYearGroup,
              sb_user_subjects: res.userSubjects,
              sb_school_name: res.schoolName,
              sb_school_country: res.schoolCountry,
              sb_email: res.email,
              sb_organisation_id: res.sbOrganisationId,
              sb_site_instance: res.sbSiteInstance
            },

            account: {
              id: accountId
            }
          });
        });
      });
  }
}
